import { z, ZodError } from 'zod'

export type kitchenState = 'OPEN' | 'CLOSED' | 'KITCHEN_BUSY' | ''
export type vendorType = 'FOOD' | 'CLOTHING'
export type operationalState = 'OPEN' | 'CLOSED' | 'MAINTENANCE' | ''
export type locationLabel = 'HOME' | 'WORK' | 'OUTLET' | 'PICKUP' | 'DROP'
export type roles = 'CUSTOMER' | 'VENDOR_OWNER' | 'DRIVER'

export type Notify = {
  success: (msg: string) => void
  error: (msg: string) => void
}

export interface Category {
  id: string
  name: string
  type: vendorType
}

export interface Vendor {
  id: string
  name: string
  type: vendorType
  profileImage: string | null
  coverImage: string | null
  totalItems: number
  availableItems: number
  isVerified: boolean
  shopState: string
}

export interface FoodItem {
  id: string
  name: string
  description: string
  price: number
  imageUrl: string | File
  isAvailable: boolean
  categories?: Category[]
}

export interface ClothingItem {
  id: string
  name: string
  description: string
  brand: string
  imageUrl?: string
  variants: ClothingVariant[]
  categories?: Category[]
}

export interface ClothingVariant {
  id: string
  size: string
  imageUrl: string | File
  imageUrls?: string[] // Additional images for multi-image gallery
  color: string
  price: number
  stockQty: number
  sku?: string
}

export type InventoryItem = FoodItem | ClothingItem

export const zodErrorMessage = ({ error }: { error: ZodError }) => {
  return error.issues.map((er) => `${er.path.join('.')}: ${er.message}`).join(', ')
}

export const accountSignupSchema = z.object({
  phone: z.string().regex(/^[6-9]\d{9}$/, 'Phone number must be a valid 10-digit Indian number'),
  name: z.string().min(3, 'name is too short'),
  role: z.enum(['CUSTOMER', 'VENDOR_OWNER']),
})

export const accountOTPVerifySchema = z.object({
  phone: z.string().regex(/^[6-9]\d{9}$/, 'Phone number must be a valid 10-digit Indian number'),
  otp: z.string().regex(/^\d{6}$/, 'OTP must be a 6-digit number'),
  role: z.enum(['CUSTOMER', 'VENDOR_OWNER', 'DRIVER']),
})

export const accountSigninSchema = z.object({
  phone: z.string().regex(/^[6-9]\d{9}$/, 'Phone number must be a valid 10-digit Indian number'),
})

export type sendEmailType = 'VENDOR_ONBOARDING_CONFIRMATION'

export const sendEmailSchema = z.object({
  email: z.string().email(),
  type: z.enum(['VENDOR_ONBOARDING_CONFIRMATION']),
})

export interface SendEmailInput {
  email: string
  type: sendEmailType
}

export const vendorCreateSchema = z.object({
  outletName: z.string(),
  type: z.enum(['FOOD', 'CLOTHING']),
  gstNumber: z.string().optional(),
  panNumber: z.string(),
  aadhaarNumber: z.string(),
  contactNumber: z.string().regex(/^[6-9]\d{9}$/, 'Contact number must be a valid 10-digit Indian number'),
})

export const createLocationSchema = z.object({
  vendorId: z.string().uuid(),
  role: z.enum(['CUSTOMER', 'VENDOR_OWNER', 'DRIVER']),
  location: z.array(
    z.object({
      latitude: z.number(),
      longitude: z.number(),
      address: z.string().min(10, 'address is too short'),
      label: z.enum(['HOME', 'WORK', 'OUTLET', 'PICKUP', 'DROP']),
    })
  ),
})

export const createClosedDaySchema = z.object({
  vendorId: z.string().uuid(),
  closedDays: z.array(z.string()),
})

export const foodVendorCreateOrUpdateSchema = z.object({
  vendorId: z.string().uuid(),
  fssaiNumber: z.string(),
  kitchenState: z.enum(['OPEN', 'CLOSED', 'KITCHEN_BUSY']),
  openingTime: z.string().optional(),
  closingTime: z.string().optional(),
  is247: z.boolean().optional(),
})

export const createFoodItemSchema = z.object({
  vendorId: z.string().uuid(),
  name: z.string().min(4, 'item name is too short'),
  description: z.string().min(20, 'item description is too short'),
  price: z.number(),
  isAvailable: z.boolean(),
  categories: z.array(z.string().uuid()).optional(),
})

export const editFoodItemSchema = z.object({
  vendorId: z.string().uuid(),
  itemId: z.string().uuid(),
  name: z.string().min(4, 'item name is too short'),
  description: z.string().min(20, 'item description is too short'),
  price: z.number(),
  isAvailable: z.boolean(),
  categories: z.array(z.string().uuid()).optional(),
})

export const clothVendorCreateOrUpdateSchema = z.object({
  vendorId: z.string().uuid(),
  returnPolicy: z.string(),
  operationalState: z.enum(['OPEN', 'CLOSED', 'MAINTAINANCE']),
})

export const createClothingProductSchema = z.object({
  vendorId: z.string().uuid(),
  name: z.string().min(4, 'item name is too short'),
  description: z.string().min(20, 'item description is too short'),
  brand: z.string(),
  variant: z.array(
    z.object({
      size: z.string(),
      color: z.string(),
      price: z.number(),
      stockQty: z.number(),
    })
  ),
  categories: z.array(z.string().uuid()).optional(),
})

export const editClothingProductSchema = z.object({
  vendorId: z.string().uuid(),
  productId: z.string().uuid(),
  name: z.string().min(4, 'item name is too short'),
  description: z.string().min(20, 'item description is too short'),
  brand: z.string(),
  variant: z.array(
    z.object({
      size: z.string(),
      color: z.string(),
      price: z.number(),
      stockQty: z.number(),
    })
  ),
  categories: z.array(z.string().uuid()).optional(),
})

export const deleteInventorySchema = z.object({
  vendorId: z.string().uuid(),
  itemId: z.string().uuid(),
  vendorType: z.enum(['FOOD', 'CLOTHING']),
})

export const createCategoriesSchema = z.object({
  vendorId: z.string().uuid(),
  name: z.array(z.string()),
  type: z.enum(['FOOD', 'CLOTHING']),
})

export const suggestionSchema = z.object({
  input: z.string().min(3),
})

export interface SendMapDataToUser {
  title: string
  shortDesc: string
  longDesc: string
  lat: number
  lng: number
}

export type UUID = string
export type ISODateString = string

export interface AccountSignupInput {
  phone: string
  name: string
  role: 'CUSTOMER' | 'VENDOR_OWNER'
}

export interface AuthMessageResponse {
  message: string
}

export interface AccountOTPVerifyInput {
  phone: string
  otp: string
  role: roles
}

export interface OTPVerifySuccessResponse {
  message: string
  data: {
    token: string
  }
}

export interface AccountSigninInput {
  phone: string
}

export interface AccountMeResponse {
  id: UUID
  name: string
  phone: string
  roles: Array<roles>
  createdAt?: string
  email?: string | null
}

export const updateAccountSchema = z.object({
  name: z.string().min(3, 'name is too short'),
})

export interface UpdateAccountInput {
  name: string
}

export interface VendorCreateInput {
  outletName: string
  type: 'FOOD' | 'CLOTHING' | ''
  gstNumber?: string
  panNumber: string
  aadhaarNumber: string
  contactNumber: string
}

export interface VendorCreateResponse {
  message: string
  data: {
    vendorId: UUID
  }
}

export interface GetVendorParams {
  vendorId: UUID
}

export interface VendorLocation {
  id: UUID
  latitude: number
  longitude: number
  address: string
  label: 'HOME' | 'WORK' | 'OUTLET' | 'PICKUP' | 'DROP'
  contactPhone: string
}

export interface CreateVendorLocationInput {
  vendorId: UUID
  role: 'CUSTOMER' | 'VENDOR_OWNER' | 'DRIVER'
  location: {
    latitude: number
    longitude: number
    address: string
    label: 'HOME' | 'WORK' | 'OUTLET' | 'PICKUP' | 'DROP'
  }[]
}

export interface CreateClosedDayInput {
  vendorId: UUID
  closedDays: string[]
}

export interface DeleteClosedDayInput {
  vendorId: UUID
  closingDayId: UUID
}

export interface FoodVendorCreateOrUpdateInput {
  vendorId: UUID
  fssaiNumber: string
  kitchenState: kitchenState
  openingTime: ISODateString | undefined
  closingTime: ISODateString | undefined
  is247: boolean
}

export interface CreateFoodItemInput {
  vendorId: UUID
  name: string
  description: string
  imageUrl: string
  price: number
  isAvailable: boolean
  categories?: string[]
}

export interface EditFoodItemInput {
  vendorId: UUID
  itemId: UUID
  name: string
  description: string
  price: number
  isAvailable: boolean
  categories?: string[]
}

export interface ClothVendorCreateOrUpdateInput {
  vendorId: UUID
  returnPolicy: string
  operationalState: operationalState
}

export interface ClothingVariantInput {
  size: string
  color: string
  price: number
  stockQty: number
  imageIndex?: number // Index in imageFiles array, undefined means use existing image
  existingImageUrl?: string // For edit: the current image URL to keep if no new image
  additionalImageIndices?: number[] // Indices for additional images
  existingAdditionalUrls?: string[] // Existing additional image URLs to keep
}

export interface CreateClothingProductInput {
  vendorId: UUID
  name: string
  description: string
  brand: string
  variant: ClothingVariantInput[]
  categories?: string[]
}

export interface EditClothingProductInput {
  vendorId: UUID
  productId: UUID
  name: string
  description: string
  brand: string
  variant: ClothingVariantInput[]
  categories?: string[]
}

export interface CreateCategoryInput {
  vendorId: UUID
  name: string[]
  type: vendorType
}

export type useCreateRoleBasedVendorProps =
  | {
      input: FoodVendorCreateOrUpdateInput
      type: 'FOOD' | ''
    }
  | {
      input: ClothVendorCreateOrUpdateInput
      type: 'CLOTHING' | ''
    }
